/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../../IEditor";
import { TOAST_TYPE } from "../../toasts/IToast";
import { ActionEditorView } from "./ActionEditorEdit/ActionEditorView";
import { LayoutState_Panel } from "../../ui/components/Layout/LayoutState";
import { CreateActionDialog } from "./CreateActionDialog/CreateActionDialog";
import { termsEditorActionEditor } from "../../terms/actionEditor";
import { ErrorTracker } from "@adapptio/lib-components";

// import { CreateExportDialog } from "./CreateActionExportDialog/CreateActionExportDialog";

export function registerCommands(dc: IEditor): void {
	const viewSvc = dc.get("viewService");
	const commandsSvc = dc.get("commandsService");
	const dialogSvc = dc.get("dialogService");
	const toastSvc = dc.get("toastService");

	commandsSvc.register({
		id: "actionEditor.create.open",
		label: termsEditorActionEditor.mainMenu.sectionCreate.action.label,
		args: [
			{
				label: "Base path",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		handler: async (args) => {
			const basePath = args[0] as string;

			const res = await dialogSvc.open(CreateActionDialog, "actionEditor.createAction", { basePath });

			if (res.resourceId) {
				commandsSvc.execute("resource.open", res.resourceId);
			}

			return res.resourceId;
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.undo",
		label: termsEditorActionEditor.commands.undo,
		handler: async () => {
			const actionEditor = viewSvc.getCurrentView() as ActionEditorView;
			actionEditor.undo();
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.redo",
		label: termsEditorActionEditor.commands.redo,
		handler: async () => {
			const actionEditor = viewSvc.getCurrentView() as ActionEditorView;
			actionEditor.redo();
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.toggleLeftPanel",
		label: termsEditorActionEditor.commands.toggleLeftPanel,
		handler: async () => {
			if (viewSvc.isViewActive("actionEditor.edit")) {
				(
					(viewSvc.getCurrentView() as ActionEditorView)
						.getLayout()
						.getById("panelLeft") as LayoutState_Panel
				)?.toggleExpanded();
			}
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.toggleRightPanel",
		label: termsEditorActionEditor.commands.toggleRightPanel,
		handler: async () => {
			if (viewSvc.isViewActive("actionEditor.edit")) {
				(
					(viewSvc.getCurrentView() as ActionEditorView)
						.getLayout()
						.getById("panelRight") as LayoutState_Panel
				)?.toggleExpanded();
			}
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.hideAllPanels",
		label: termsEditorActionEditor.commands.hideAllPanels,
		handler: async () => {
			if (viewSvc.isViewActive("actionEditor.edit")) {
				const layout = (viewSvc.getCurrentView() as ActionEditorView).getLayout();

				(layout.getById("panelLeft") as LayoutState_Panel)?.collapse();
				(layout.getById("panelRight") as LayoutState_Panel)?.collapse();
			}
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.toggleConsole",
		label: termsEditorActionEditor.commands.toggleConsole,
		handler: async () => {
			if (viewSvc.isViewActive("actionEditor.edit")) {
				(
					(viewSvc.getCurrentView() as ActionEditorView)
						.getLayout()
						.getById("console") as LayoutState_Panel
				)?.toggleExpanded();
			}
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.saveResource",
		label: termsEditorActionEditor.commands.saveResource,
		handler: async () => {
			if (viewSvc.isViewActive("actionEditor.edit")) {
				const actionEditor = viewSvc.getCurrentView() as ActionEditorView;

				if (!actionEditor.getIsModified() || actionEditor.isBeingSaved()) {
					return;
				}

				const loadingToast = toastSvc.add({
					type: TOAST_TYPE.LOADING,
					message: termsEditorActionEditor.toasts.savingAction,
					isClosable: false
				});

				try {
					await actionEditor.save();

					toastSvc.add({
						type: TOAST_TYPE.SUCCESS,
						message: termsEditorActionEditor.toasts.actionHasBeenSaved,
						timeoutMs: 2500
					});
				} catch (err) {
					const errorTracker = ErrorTracker.getInstance();
					errorTracker.captureException(err);

					toastSvc.add({
						type: TOAST_TYPE.ERROR,
						message: termsEditorActionEditor.toasts.failedToSaveAction,
						details: String(err)
					});
				} finally {
					toastSvc.remove(loadingToast);
				}
			}
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.openHistory",
		label: termsEditorActionEditor.other.historyNotAvailableYet,
		handler: async () => {
			toastSvc.add({
				type: TOAST_TYPE.INFO,
				message: "History is not implemented yet."
			});
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.saveExecute",
		// @todo Translate
		label: "Save & Execute Action",
		handler: async () => {
			if (viewSvc.isViewActive("actionEditor.edit")) {
				const view = viewSvc.getCurrentView() as ActionEditorView;

				try {
					await view.save();
					await view.execute();
				} catch (err) {
					const errorTracker = ErrorTracker.getInstance();
					errorTracker.captureException(err);

					toastSvc.add({
						type: TOAST_TYPE.ERROR,
						message: "Failed to save and execute the action.",
						details: String(err)
					});
				}
			}
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.flowEditor.zoomIn",
		// @todo Translate
		label: "Zoom In",
		handler: async () => {
			if (viewSvc.isViewActive("actionEditor.edit")) {
				const view = viewSvc.getCurrentView() as ActionEditorView;
				view.flowEditorZoomIn();
			}
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.flowEditor.zoomOut",
		// @todo Translate
		label: "Zoom Out",
		handler: async () => {
			if (viewSvc.isViewActive("actionEditor.edit")) {
				const view = viewSvc.getCurrentView() as ActionEditorView;
				view.flowEditorZoomOut();
			}
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.flowEditor.zoomReset",
		// @todo Translate
		label: "Reset Zoom",
		handler: async () => {
			if (viewSvc.isViewActive("actionEditor.edit")) {
				const view = viewSvc.getCurrentView() as ActionEditorView;
				view.flowEditorZoomReset();
			}
		}
	});

	commandsSvc.register({
		id: "actionEditor.edit.flowEditor.zoomFit",
		// @todo Translate
		label: "Fit Nodes into View",
		handler: async () => {
			if (viewSvc.isViewActive("actionEditor.edit")) {
				const view = viewSvc.getCurrentView() as ActionEditorView;
				view.flowEditorZoomFit();
			}
		}
	});
}
